import React, { useEffect, useMemo } from 'react';
import {
    match as Match,
    RouteProps,
    matchPath,
    useHistory,
    useRouteMatch,
    withRouter,
} from 'react-router-dom';
import { useScrollTopManager } from 'contexts/useScrollTopManager';

interface DialogRouteProps extends Omit<RouteProps, 'render'> {
    onClose?: () => void;
}

const useDialogRouteProps = (props: DialogRouteProps) => {
    const match = useRouteMatch();
    const { registerExclusion, unRegisterExclusion } = useScrollTopManager();
    const history = useHistory();
    const { path, component, onClose: onCloseConsumer, ...routeProps } = props;

    const Component = useMemo(
        () => (component ? withRouter(component) : React.Fragment) as ReturnType<typeof withRouter>,
        [component],
    );

    const onClose = useMemo(
        () =>
            <T>(fn?: (...args: any) => T) => {
                onCloseConsumer?.();
                history.push(match.url);
                !!fn && fn();
            },
        [history, match.url, onCloseConsumer],
    );

    useEffect(() => {
        registerExclusion([match.path, ...routePathToArray(path)]);
        return () => {
            unRegisterExclusion([match.path, ...routePathToArray(path)]);
        };
    }, [match.path, path, registerExclusion, unRegisterExclusion]);

    return {
        path,
        component,
        Component,
        onClose,
        ...routeProps,
    };
};

const routePathToArray = (path: RouteProps['path']) => [...(Array.isArray(path) ? path : [path])];
const isOpenFromMatch = (match: Match | null, path: RouteProps['path']) =>
    routePathToArray(path).some(
        p =>
            !!match &&
            !!matchPath(match.url, {
                path: p,
                exact: false,
                strict: false,
            })?.isExact,
    );

export { useDialogRouteProps, routePathToArray, isOpenFromMatch };
export type { DialogRouteProps };
